import SimplePeer from 'simple-peer';
import { Socket } from 'socket.io-client';

// ── Call diagnostic logger ──────────────────────────────────────────────────
// Mirrors to the browser console AND ships lines to the backend so they're
// viewable in the admin panel — mobile users can't open a browser console.
// Batched: lines buffer and flush ~1.5s after the last log, capped per request.
const _logBuffer: string[] = [];
let _flushTimer: ReturnType<typeof setTimeout> | null = null;

function _stringify(a: any): string {
  if (typeof a === 'string') return a;
  try { return JSON.stringify(a); } catch { return String(a); }
}

async function _flushLogs() {
  _flushTimer = null;
  if (!_logBuffer.length) return;
  const lines = _logBuffer.splice(0, _logBuffer.length);
  try {
    await fetch('/api/auth/call-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ lines }),
    });
  } catch (_) { /* drop on failure — never block the call */ }
}

function _capture(level: string, args: any[]) {
  const ts = new Date().toISOString().slice(11, 23); // HH:MM:SS.mmm
  _logBuffer.push(`${ts} [${level}] ${args.map(_stringify).join(' ')}`);
  if (_logBuffer.length > 200) _logBuffer.shift();
  if (!_flushTimer) _flushTimer = setTimeout(_flushLogs, 1500);
}

function vlog(...args: any[])  { console.log(...args);   _capture('log', args); }
function vwarn(...args: any[]) { console.warn(...args);  _capture('warn', args); }
function verr(...args: any[])  { console.error(...args); _capture('error', args); }

export type CallState = 'idle' | 'calling' | 'ringing' | 'connected' | 'ended';
export type EndReason = 'rejected' | 'busy' | 'unavailable' | 'hangup' | 'error' | null;

export interface CallerInfo {
  userId: string;
  name: string;
  avatar?: string;
}

export interface CallCallbacks {
  onStateChange: (state: CallState) => void;
  onRemoteStream: (stream: MediaStream) => void;
  onIncomingCall: (callerInfo: CallerInfo) => void;
  onError: (err: Error) => void;
  onEndReason?: (reason: EndReason) => void;
  /** Non-fatal issue — the call proceeds, but the user should be told (e.g. camera unavailable). */
  onWarning?: (message: string) => void;
}

// Metered.ca managed TURN (*.metered.live) issues TIME-LIMITED credentials that
// MUST be fetched from their REST API — using the API key as a static
// username/credential fails TURN auth, which makes calls "connect" (SDP
// negotiates) but relay no media → black screen + no audio.
// Set REACT_APP_TURN_DOMAIN (e.g. broodbook.metered.live) and REACT_APP_TURN_API_KEY.
// Fallback: REACT_APP_TURN_URL / _USERNAME / _CREDENTIAL for a custom/coturn server.
const TURN_DOMAIN = process.env.REACT_APP_TURN_DOMAIN;
const TURN_API_KEY = process.env.REACT_APP_TURN_API_KEY;

// True if any TURN (not just STUN) is configured — used by the timeout diagnostic.
const TURN_CONFIGURED = !!((TURN_DOMAIN && TURN_API_KEY) || process.env.REACT_APP_TURN_URL);

const STUN_FALLBACK: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  ...(process.env.REACT_APP_TURN_URL
    ? [{ urls: process.env.REACT_APP_TURN_URL, username: process.env.REACT_APP_TURN_USERNAME || '', credential: process.env.REACT_APP_TURN_CREDENTIAL || '' }]
    : [])
];

// Cache the fetched ICE servers for the session (Metered creds are valid for hours).
let cachedIceServers: RTCIceServer[] | null = null;

async function getIceServers(): Promise<RTCIceServer[]> {
  if (cachedIceServers) return cachedIceServers;
  if (!TURN_DOMAIN || !TURN_API_KEY) {
    vlog('[VideoCall] No Metered TURN configured — using STUN fallback only');
    return STUN_FALLBACK;
  }
  try {
    const url = `https://${TURN_DOMAIN}/api/v1/turn/credentials?apiKey=${encodeURIComponent(TURN_API_KEY)}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Metered API returned ${res.status}`);
    const servers = await res.json();
    if (Array.isArray(servers) && servers.length) {
      cachedIceServers = servers;
      const turnCount = servers.filter((s: RTCIceServer) => {
        const u = s.urls; const arr = Array.isArray(u) ? u : [u];
        return arr.some(x => /^turns?:/.test(x));
      }).length;
      vlog(`[VideoCall] Fetched ${servers.length} ICE servers from Metered (${turnCount} TURN)`);
      return servers;
    }
    throw new Error('Metered API returned empty/invalid list');
  } catch (e) {
    vwarn('[VideoCall] Metered TURN fetch failed — falling back to STUN:', e);
    return STUN_FALLBACK;
  }
}

const VIDEO_SIGNAL_EVENT = 'video-signal';
const CONNECTION_TIMEOUT_MS = 30_000;

export class VideoCallService {
  private socket: Socket | null = null;
  private peer: SimplePeer.Instance | null = null;
  private localStream: MediaStream | null = null;
  private callbacks: CallCallbacks | null = null;
  private currentUserId: string = '';
  private activePeerId: string = '';
  private state: CallState = 'idle';
  private pendingSignals: any[] = [];
  private endedTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionTimer: ReturnType<typeof setTimeout> | null = null;
  private endReason: EndReason = null;
  private iceRestartAttempts = 0;
  private static readonly MAX_ICE_RESTARTS = 3;

  private _onCallRequest = ({ callerId, callerInfo }: { callerId: string; callerInfo: CallerInfo }) => {
    if (this.state !== 'idle') {
      this.socket?.emit('call-busy', { callerId });
      return;
    }
    this.activePeerId = String(callerId);
    this._setState('ringing');
    this.callbacks?.onIncomingCall(callerInfo);
  };

  private _onCallAccept = async ({ accepterId }: { accepterId: string }) => {
    this.activePeerId = String(accepterId);
    await this._startPeer(true);
  };

  private _onCallReject     = () => { this._endCall('rejected'); };
  private _onCallEnd        = () => { this._endCall('hangup'); };
  private _onCallBusy       = () => { this._endCall('busy'); };
  private _onCallUnavailable = () => { this._endCall('unavailable'); };

  private _onVideoSignal = ({ fromUserId, signal }: { fromUserId: string; signal: any }) => {
    vlog('[VideoCall] signal in from', fromUserId, 'type:', signal?.type || 'candidate', 'activePeer:', this.activePeerId, 'hasPeer:', !!this.peer);
    if (String(fromUserId) !== this.activePeerId) return;
    if (this.peer) {
      try { this.peer.signal(signal); } catch (e) { verr('[VideoCall] signal apply error:', e); }
    } else {
      this.pendingSignals.push(signal);
    }
  };

  // ── Public API ────────────────────────────────────────────────────────────

  init(socket: Socket, userId: string, callbacks: CallCallbacks) {
    this._unbindSocketEvents();
    this.socket = socket;
    this.currentUserId = userId;
    this.callbacks = callbacks;
    this._bindSocketEvents();

    socket.on('reconnect', () => {
      this._unbindSocketEvents();
      this._bindSocketEvents();
    });
  }

  async initiateCall(targetUserId: string, callerInfo: CallerInfo) {
    if (this.state !== 'idle') return;
    this.activePeerId = String(targetUserId);
    this._setState('calling');
    this.socket?.emit('call-request', { targetUserId, callerInfo });
  }

  // Callee: acquire media + create peer first, THEN emit call-accept.
  // This ensures the peer exists and can handle the initiator's first signals
  // without relying on the pendingSignals buffer.
  async acceptCall() {
    if (this.state !== 'ringing') {
      vwarn('[VideoCall] acceptCall ignored — state was', this.state, '(expected "ringing"). Likely caused by a socket flap that ended the call before Accept was pressed.');
      this.callbacks?.onError(new Error('Call no longer active — the connection dropped briefly. Ask them to call again.'));
      return;
    }
    if (!this.activePeerId) {
      vwarn('[VideoCall] acceptCall: activePeerId is empty — cannot route call-accept');
      this.callbacks?.onError(new Error('Call info lost — please ask them to call again.'));
      this._endCall('error');
      return;
    }
    await this._startPeer(false);
    this.socket?.emit('call-accept', { callerId: this.activePeerId });
  }

  rejectCall() {
    if (this.state !== 'ringing') return;
    this.socket?.emit('call-reject', { callerId: this.activePeerId });
    this._peerCleanup();
    this._setState('idle');
  }

  hangUp() {
    const peerId = this.activePeerId;
    this._peerCleanup();
    this.endReason = 'hangup';
    this.callbacks?.onEndReason?.('hangup');
    this._setState('ended');
    if (peerId) this.socket?.emit('call-end', { peerId });
    this._scheduleIdle();
  }

  getLocalStream(): MediaStream | null { return this.localStream; }
  getState(): CallState { return this.state; }
  getEndReason(): EndReason { return this.endReason; }

  // Track which camera is active so we can toggle without reading getSettings()
  private _facingMode: 'user' | 'environment' = 'user';

  /** Flip between front and rear camera during a connected call. */
  async switchCamera(): Promise<void> {
    if (!this.localStream || !this.peer) return;
    const currentTrack = this.localStream.getVideoTracks()[0];
    if (!currentTrack) return;

    const nextFacing = this._facingMode === 'user' ? 'environment' : 'user';

    // Android Chrome won't release the active camera while a track is live, so a
    // second getUserMedia() call for the opposite facing-mode is often ignored
    // and returns the SAME camera. Stop the current track FIRST to free the
    // hardware, then request the new one.
    currentTrack.stop();
    this.localStream.removeTrack(currentTrack);

    // Try `exact` first so we actually get the opposite camera (some Android
    // devices honour `ideal` only as a hint and return what's already warm).
    // Fall back to `ideal` if the device truly lacks a camera on that side.
    let newStream: MediaStream | null = null;
    try {
      newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: nextFacing } },
        audio: false,
      });
    } catch (_) {
      try {
        newStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: nextFacing } },
          audio: false,
        });
      } catch (e) {
        vwarn('[VideoCall] switchCamera failed:', e);
        return;
      }
    }

    const newTrack = newStream.getVideoTracks()[0];
    if (!newTrack) return;

    // Replace track in the live peer connection (no renegotiation needed)
    const pc = (this.peer as any)._pc as RTCPeerConnection | undefined;
    if (pc) {
      const sender = pc.getSenders().find(s => s.track?.kind === 'video');
      if (sender) await sender.replaceTrack(newTrack);
    }

    // Stop any extra tracks on the temp stream we don't need, then attach the new
    // video track to the local stream so the self-preview updates.
    // (currentTrack was already stopped/removed before getUserMedia above.)
    newStream.getTracks().forEach(t => { if (t !== newTrack) { try { t.stop(); } catch (_) {} } });
    this.localStream.addTrack(newTrack);

    this._facingMode = nextFacing;
  }

  destroy() {
    if (this.endedTimer) clearTimeout(this.endedTimer);
    if (this.connectionTimer) clearTimeout(this.connectionTimer);
    this._unbindSocketEvents();
    this._peerCleanup();
    this.callbacks = null;
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  private _setState(s: CallState) {
    this.state = s;
    this.callbacks?.onStateChange(s);
  }

  private _scheduleIdle(ms = 2000) {
    if (this.endedTimer) clearTimeout(this.endedTimer);
    this.endedTimer = setTimeout(() => {
      this.endedTimer = null;
      this.endReason = null;
      this.callbacks?.onEndReason?.(null);
      this._setState('idle');
    }, 2000);
  }

  private _endCall(reason: EndReason = 'hangup') {
    this._cancelConnectionTimeout();
    this._peerCleanup();
    this.endReason = reason;
    this.callbacks?.onEndReason?.(reason);
    this._setState('ended');
    // Show error messages longer so the user can read them
    this._scheduleIdle(reason === 'error' ? 8000 : 2000);
  }

  private _startConnectionTimeout() {
    this._cancelConnectionTimeout();
    this.connectionTimer = setTimeout(() => {
      if (this.state !== 'connected') {
        this.callbacks?.onError(new Error(
          TURN_CONFIGURED
            ? 'Call timed out — could not establish a connection. Check both users have internet, then try again.'
            : 'Call timed out — no TURN server configured. Cross-network calls require a TURN server. Set REACT_APP_TURN_DOMAIN + REACT_APP_TURN_API_KEY in the deployment env.'
        ));
        // Use _endCall('error') (not hangUp()) so the UI shows the timeout
        // reason instead of a plain "Call ended" message, but still notify
        // the other side so their call doesn't keep ringing.
        const peerId = this.activePeerId;
        this._endCall('error');
        if (peerId) this.socket?.emit('call-end', { peerId });
      }
    }, CONNECTION_TIMEOUT_MS);
  }

  private _cancelConnectionTimeout() {
    if (this.connectionTimer) { clearTimeout(this.connectionTimer); this.connectionTimer = null; }
  }

  private _bindSocketEvents() {
    if (!this.socket) return;
    this.socket.on('call-request',     this._onCallRequest);
    this.socket.on('call-accept',      this._onCallAccept);
    this.socket.on('call-reject',      this._onCallReject);
    this.socket.on('call-end',         this._onCallEnd);
    this.socket.on('call-busy',        this._onCallBusy);
    this.socket.on('call-unavailable', this._onCallUnavailable);
    this.socket.on(VIDEO_SIGNAL_EVENT, this._onVideoSignal);
  }

  private _unbindSocketEvents() {
    if (!this.socket) return;
    this.socket.off('call-request',     this._onCallRequest);
    this.socket.off('call-accept',      this._onCallAccept);
    this.socket.off('call-reject',      this._onCallReject);
    this.socket.off('call-end',         this._onCallEnd);
    this.socket.off('call-busy',        this._onCallBusy);
    this.socket.off('call-unavailable', this._onCallUnavailable);
    this.socket.off(VIDEO_SIGNAL_EVENT, this._onVideoSignal);
  }

  private async _startPeer(initiator: boolean) {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.callbacks?.onError(new Error('Media devices unavailable — site must be served over HTTPS'));
      this._endCall('error');
      return;
    }

    if (navigator.permissions?.query) {
      try {
        const mic = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (mic.state === 'denied') {
          this.callbacks?.onError(new Error('Microphone permission denied — enable it in browser/app settings'));
          this._endCall('error');
          return;
        }
      } catch (_) {}
    }

    // Acquire media, then check if the call is still live before proceeding.
    // Without this guard, a stream acquired during a cancelled call leaks
    // and keeps the browser mic/cam indicator on.
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    } catch {
      // Some browsers (notably iPadOS Safari) throw OverconstrainedError/NotReadableError
      // for `video: true` while a more specific constraint succeeds — retry with an
      // explicit facingMode before giving up on video entirely.
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: true });
      } catch {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
          this.callbacks?.onWarning?.('Camera unavailable — connected with audio only. Check camera permissions and try calling again.');
        } catch (err: any) {
          const n = err?.name || '';
          this.callbacks?.onError(new Error(
            n === 'NotAllowedError' ? 'Camera/microphone access denied — click the camera icon in your browser address bar to allow, then call again' :
            n === 'NotFoundError'  ? 'No microphone found on this device' :
            `Could not access camera/microphone (${n || 'unknown error'})`
          ));
          this._endCall('error');
          return;
        }
      }
    }

    if (this.state === 'idle' || this.state === 'ended') {
      stream?.getTracks().forEach(t => t.stop());
      return;
    }
    this.localStream = stream;

    // Fetch real (time-limited) TURN credentials before negotiating.
    const iceServers = await getIceServers();

    // Re-check state — the await above may have spanned a hangup.
    const stateAfterFetch = this.state as string;
    if (stateAfterFetch === 'idle' || stateAfterFetch === 'ended') {
      this.localStream?.getTracks().forEach(t => t.stop());
      this.localStream = null;
      return;
    }

    vlog('[VideoCall] ICE servers:', JSON.stringify(iceServers.map(s => s.urls)));
    vlog('[VideoCall] Creating peer, initiator:', initiator, 'localStream tracks:', this.localStream.getTracks().map(t => `${t.kind}:${t.readyState}`));

    this.peer = new SimplePeer({
      initiator,
      stream: this.localStream,
      trickle: true,
      config: { iceServers },
    });

    this._startConnectionTimeout();

    while (this.pendingSignals.length) {
      try { this.peer.signal(this.pendingSignals.shift()); } catch (_) {}
    }

    this.peer.on('signal', (data) => {
      vlog('[VideoCall] signal out:', data.type || 'candidate');
      this.socket?.emit(VIDEO_SIGNAL_EVENT, { targetUserId: this.activePeerId, signal: data });
    });

    this.peer.on('stream', (remoteStream) => {
      vlog('[VideoCall] GOT remote stream, tracks:', remoteStream.getTracks().map(t => `${t.kind}:${t.readyState}:enabled=${t.enabled}`));
      this._cancelConnectionTimeout();
      this.iceRestartAttempts = 0;
      this._setState('connected');
      this.callbacks?.onRemoteStream(remoteStream);
    });

    this.peer.on('connect', () => {
      vlog('[VideoCall] peer DATA channel connected');
    });

    this.peer.on('error', (err) => {
      verr('[VideoCall] peer error:', err.message || err);
      this.callbacks?.onError(err);
      this._endCall('error');
    });

    this.peer.on('close', () => {
      vlog('[VideoCall] peer closed, state was:', this.state);
      if (this.state === 'connected' || this.state === 'calling' || this.state === 'ringing') {
        this._endCall();
      }
    });

    const pc = (this.peer as any)?._pc as RTCPeerConnection | undefined;
    if (pc) {
      pc.addEventListener('iceconnectionstatechange', () => {
        vlog('[VideoCall] ICE state:', pc.iceConnectionState);
        if (this.state !== 'connected' || !this.peer) return;
        const iceState = pc.iceConnectionState;
        if (iceState === 'disconnected' || iceState === 'failed') {
          if (this.iceRestartAttempts < VideoCallService.MAX_ICE_RESTARTS) {
            this.iceRestartAttempts++;
            this.callbacks?.onWarning?.('Connection interrupted — reconnecting…');
            try { pc.restartIce(); } catch (_) {}
          } else {
            this._endCall('error');
            this.callbacks?.onError(new Error('Connection lost after multiple reconnection attempts.'));
          }
        } else if (iceState === 'connected' || iceState === 'completed') {
          this.iceRestartAttempts = 0;
        }
      });
      pc.addEventListener('icegatheringstatechange', () => {
        vlog('[VideoCall] ICE gathering:', pc.iceGatheringState);
      });
      pc.addEventListener('connectionstatechange', () => {
        vlog('[VideoCall] connection state:', pc.connectionState);
      });
      pc.addEventListener('track', (ev) => {
        vlog('[VideoCall] track event:', ev.track.kind, ev.track.readyState, 'streams:', ev.streams.length);
      });
    } else {
      vwarn('[VideoCall] Could not access underlying RTCPeerConnection for monitoring');
    }
  }

  private _peerCleanup() {
    this._cancelConnectionTimeout();

    // CRITICAL ORDER: release the mic/camera at the RTCPeerConnection sender level
    // BEFORE destroying the peer. If we just call peer.destroy() with senders still
    // attached, Chrome/Edge can hold the mic indicator (and block other mic users
    // like the browser's speech-recognition API) for 30s+ after the call ends.
    try {
      const pc = (this.peer as any)?._pc as RTCPeerConnection | undefined;
      if (pc) {
        pc.getSenders().forEach(s => {
          try { s.track?.stop(); } catch (_) {}
          try { pc.removeTrack(s); } catch (_) {}
        });
        pc.getReceivers().forEach(r => { try { r.track?.stop(); } catch (_) {} });
      }
    } catch (_) {}

    // Stop the local stream's tracks BEFORE destroy() — defensive in case the
    // stream had tracks that were never wired into the peer connection.
    this.localStream?.getTracks().forEach(t => { try { t.stop(); } catch (_) {} });

    try { this.peer?.destroy(); } catch (_) {}
    this.peer = null;
    this.localStream = null;
    this.activePeerId = '';
    this.pendingSignals = [];
    this._facingMode = 'user';
    this.iceRestartAttempts = 0;
  }
}

export const videoCallService = new VideoCallService();
